import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  Link,
  Text,
  Tooltip,
  useDisclosure,
  usePrevious,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Input, Switch } from '@gamma/ui/form-fields';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import ErrorSharpIcon from '@gamma/ui/icons/ErrorSharpIcon';
import VisibilityOffSharpIcon from '@gamma/ui/icons/VisibilityOffSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { Modal } from '@gamma/ui/overlay';
import { isEqual } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { URLS } from '../../../../shared/constants';
import { useLogOperationCall } from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers/AuthProvider';
import {
  EscalationConfigurationInput,
  IVerifyEscalationConfiguration,
  QUERY_ESCALATION_CONFIGURATIONS,
  ServiceNowEscalationConfiguration,
  useDeleteEscalationConfigurations,
  useQueryEscalationConfigurations,
  useQueryEscalationConfigurationsPublic,
  useSaveEscalationConfigurations,
  useVerifyEscalationConfiguration,
} from '../../queries';

const { integrations } = i18n.pages;
const {
  save,
  cancel,
  error,
  success,
  warning,
  noGoBack,
  yesDelete,
  enabled: enabledLabel,
  disabled: disabledLabel,
  connectionVerified: connectionVerifiedMessage,
  connectionNotVerified,
} = integrations;

const {
  deleteResult,
  deleteWarning,
  saveSuccess,
  saveError,
  deleteError,
  deleteSuccess,
  tableIDInfo,
  forMoreInfo,
  instanceIDInfo,
  enterCredentials,
  verifyConnection,
  serviceNowFormFIelds,
  serviceNowIntegration,
  deleteServiceNowConnection,
  youCanSendDetectionsManually,
  serviceNowDocumentation,
  detectionsCanNotBeEscalatedToServiceNow,
} = integrations.serviceNow;

const { SERVICENOW_DOCS } = URLS;

interface ServiceNowSettingsCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceNowConfiguration?: ServiceNowEscalationConfiguration;
  isEnableChanged?: boolean;
  setIsEnableChanged?: Dispatch<SetStateAction<boolean>>;
}

export const ServiceNowSettingsCardModal = ({
  isOpen,
  onClose,
}: ServiceNowSettingsCardModalProps) => {
  const showToast = useToast();
  const logOperationCall = useLogOperationCall();

  const [isEnableChanged, setIsEnableChanged] = useState<boolean>(false);

  const { userRole, setUserPrivileges, userPrivileges, userLicense } =
    useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};
  const escalationConfigurationQuery =
    userRole === 'admin'
      ? useQueryEscalationConfigurations
      : useQueryEscalationConfigurationsPublic;

  const emptyForm = {
    enabled: false,
    instance_name: '',
    password: '',
    table_name: '',
    username: '',
  };
  const { data: serviceNowData } = escalationConfigurationQuery({});

  const serviceNowConfiguration =
    serviceNowData?.queryEscalationConfigurations
      ?.escalation_configurations?.[0];

  const {
    enabled,
    instance_name,
    password,
    table_name,
    username,
    service_name,
    escalation_configuration_id,
  } = serviceNowConfiguration || {};

  const defaultFormDataValues = {
    enabled: enabled || false,
    instance_name: instance_name || '',
    password: password || '',
    table_name: table_name || '',
    username: username || '',
  };

  const {
    watch,
    control,
    register,
    formState: { isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultFormDataValues,
  });

  const formData = watch();
  const previousFormData = usePrevious(formData);

  const {
    isOpen: deleteIsOpen,
    onOpen: deleteOnOpen,
    onClose: deleteOnClose,
  } = useDisclosure();

  const [pwInputType, setPwInputType] = useState<boolean>(true);

  const [configuration, setConfiguration] = useState<
    Omit<EscalationConfigurationInput, 'escalation_configuration_id'>
  >({
    enabled: false,
    service_name: service_name || 'servicenow',
    servicenow: {
      username: '',
      password: '',
      instance_name: '',
      table_name: '',
    },
  });

  const [configurationVerified, setConfigurationVerified] = useState<
    boolean | undefined
  >();

  const [
    verifyEscalationConfiguration,
    { loading: verifyEscalationConfigurationLoading },
  ] = useVerifyEscalationConfiguration({
    fetchPolicy: 'network-only',
    variables: {
      escalation_configuration: {
        ...configuration,
      },
    },
    onCompleted: (data: IVerifyEscalationConfiguration) => {
      setConfigurationVerified(data?.verifyEscalationConfiguration?.verified);
    },
  });

  const handleVerifyEscalationConfiguration = async () => {
    verifyEscalationConfiguration();
  };

  const [
    saveEscalationConfigurations,
    { loading: saveEscalationConfigurationsLoading },
  ] = useSaveEscalationConfigurations({
    variables: {
      escalation_configurations: [
        {
          escalation_configuration_id,
          ...configuration,
        },
      ],
    },
    refetchQueries: [
      {
        query: QUERY_ESCALATION_CONFIGURATIONS,
      },
    ],
    onCompleted: async () => {
      showToast({
        title: success,
        description: saveSuccess,
        status: 'success',
        isClosable: true,
        position: 'bottom-right',
      });
      if (isEnableChanged && enabled !== formData.enabled) {
        const updatedInput = {
          status: formData.enabled ? 'Enabled' : 'Disabled',
          service_name: 'ServiceNow',
        };
        logOperationCall(
          'ENABLE_DISABLE_CONFIGURATION',
          'INTEGRATION',
          JSON.stringify(updatedInput),
        );
      }
      const newUserPrivileges = { ...userPrivileges };
      newUserPrivileges[tenant ?? '']['service-now'] = formData.enabled;
      setUserPrivileges(newUserPrivileges);
      onClose();
    },
    onError: async () => {
      showToast({
        title: error,
        description: saveError,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const handleSaveEscalationConfigurations = async () => {
    await saveEscalationConfigurations();
    setConfigurationVerified(undefined);
  };

  const [
    deleteEscalationConfigurations,
    { loading: deleteEscalationConfigurationsLoading },
  ] = useDeleteEscalationConfigurations({
    variables: {
      escalation_configuration_ids: [escalation_configuration_id],
    },
    refetchQueries: [
      {
        query: QUERY_ESCALATION_CONFIGURATIONS,
      },
    ],
    onCompleted: async () => {
      showToast({
        title: success,
        description: deleteSuccess,
        status: 'success',
        isClosable: true,
        position: 'bottom-right',
      });
      const newUserPrivileges = { ...userPrivileges };
      newUserPrivileges[tenant ?? '']['service-now'] = false;
      setUserPrivileges(newUserPrivileges);
      reset(emptyForm);
      onClose();
    },
    onError: async () => {
      showToast({
        title: error,
        description: deleteError,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const handleDeleteEscalationConfigurations = async () => {
    await deleteEscalationConfigurations();
    setConfigurationVerified(undefined);
  };

  const handleEnableDisableConfigurations = () => {
    setIsEnableChanged(!isEnableChanged);
  };

  const handleClose = () => {
    if (serviceNowConfiguration === null) {
      reset(emptyForm);
    } else if (serviceNowConfiguration) {
      reset(serviceNowConfiguration);
    }
    onClose();
  };

  useEffect(() => {
    if (
      previousFormData !== undefined &&
      !isEqual(previousFormData, formData)
    ) {
      setConfigurationVerified(undefined);
    }
  }, [formData, previousFormData]);

  useEffect(() => {
    if (isOpen && serviceNowConfiguration) {
      reset(serviceNowConfiguration);
    }
  }, [isOpen, serviceNowConfiguration, reset]);

  return (
    <>
      <Modal
        size="lg"
        isOpen={isOpen}
        onClose={handleClose}
        isCentered={true}
        title={serviceNowIntegration}
        closeOnEsc={!verifyEscalationConfigurationLoading}
        isCloseDisabled={verifyEscalationConfigurationLoading}
        closeOnOverlayClick={!verifyEscalationConfigurationLoading}
        body={
          <VStack as="form" alignItems="flex-start" spacing={4}>
            <Text textStyle="body-md">{youCanSendDetectionsManually}</Text>
            <Controller
              name="enabled"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Switch
                  inline
                  name={name}
                  isChecked={value}
                  isDisabled={false}
                  onChange={(e) => {
                    onChange(e);
                    handleEnableDisableConfigurations();
                  }}
                  label={formData.enabled ? enabledLabel : disabledLabel}
                />
              )}
            />
            <Box>
              <Text>
                {`${enterCredentials} `}
                <Link
                  isExternal
                  href={SERVICENOW_DOCS}
                  textDecoration="underline"
                >
                  {serviceNowDocumentation}
                </Link>
                {` ${forMoreInfo}`}
              </Text>
            </Box>
            <Input
              {...register('username', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={serviceNowFormFIelds.username}
            />
            <Input
              {...register('password', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              label={serviceNowFormFIelds.password}
              type={pwInputType ? 'password' : 'text'}
              rightElement={
                <IconButton
                  cursor="pointer"
                  aria-label={`${pwInputType ? 'Show' : 'Hide'} Password`}
                  onClick={() => setPwInputType(!pwInputType)}
                >
                  {pwInputType ? (
                    <VisibilitySharpIcon size="sm" />
                  ) : (
                    <VisibilityOffSharpIcon size="sm" />
                  )}
                </IconButton>
              }
            />
            <Input
              {...register('instance_name', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              tooltip={instanceIDInfo}
              label={serviceNowFormFIelds.instanceID}
            />
            <Input
              {...register('table_name', {
                required: true,
              })}
              isRequired={true}
              isDisabled={false}
              tooltip={tableIDInfo}
              label={serviceNowFormFIelds.tableName}
            />
            <ButtonGroup
              w="100%"
              justifyContent="space-between"
              isDisabled={!isValid || configurationVerified}
            >
              <Button
                variant="solid"
                colorScheme="gray"
                leftIcon={<CheckCircleSharpIcon />}
                onClick={async () => {
                  await setConfiguration({
                    enabled: formData?.enabled,
                    service_name: service_name || 'servicenow',
                    servicenow: {
                      username: formData?.username,
                      password: formData?.password,
                      instance_name: formData?.instance_name,
                      table_name: formData?.table_name,
                    },
                  });
                  handleVerifyEscalationConfiguration();
                }}
                isDisabled={!isValid || configurationVerified}
                isLoading={verifyEscalationConfigurationLoading}
              >
                {verifyConnection}
              </Button>
              <Tooltip label={deleteServiceNowConnection}>
                <IconButton
                  variant="solid"
                  colorScheme={
                    !escalation_configuration_id ||
                    deleteEscalationConfigurationsLoading
                      ? 'gray'
                      : 'red'
                  }
                  onClick={deleteOnOpen}
                  icon={<DeleteSharpIcon />}
                  aria-label={deleteServiceNowConnection}
                  isLoading={deleteEscalationConfigurationsLoading}
                  isDisabled={
                    !escalation_configuration_id ||
                    deleteEscalationConfigurationsLoading
                  }
                />
              </Tooltip>
            </ButtonGroup>
            {configurationVerified === true && (
              <Alert status="success" w="100%">
                <Flex>
                  <CheckCircleSharpIcon color="green.100" mr={2} />
                  <Box>
                    <Text textStyle="body-md-bold">
                      {connectionVerifiedMessage}
                    </Text>
                  </Box>
                </Flex>
              </Alert>
            )}
            {configurationVerified === false && (
              <Alert status="error" w="100%">
                <Flex>
                  <WarningSharpIcon color="red.100" mr={2} />
                  <Box>
                    <Text textStyle="body-md-bold">
                      {connectionNotVerified}
                    </Text>
                    <Text>{detectionsCanNotBeEscalatedToServiceNow}</Text>
                  </Box>
                </Flex>
              </Alert>
            )}
          </VStack>
        }
        footer={
          <ButtonGroup isDisabled={saveEscalationConfigurationsLoading}>
            <Button variant="solid" colorScheme="gray" onClick={handleClose}>
              {cancel}
            </Button>
            <Button
              variant="solid"
              onClick={async () => {
                await setConfiguration({
                  enabled: formData?.enabled,
                  service_name: service_name || 'servicenow',
                  servicenow: {
                    username: formData?.username,
                    password: formData?.password,
                    instance_name: formData?.instance_name,
                    table_name: formData?.table_name,
                  },
                });
                handleSaveEscalationConfigurations();
              }}
              isLoading={saveEscalationConfigurationsLoading}
              isDisabled={
                saveEscalationConfigurationsLoading ||
                !isValid ||
                (formData?.enabled && !configurationVerified)
              }
            >
              {save}
            </Button>
          </ButtonGroup>
        }
      />
      <Modal
        size="lg"
        isOpen={deleteIsOpen}
        onClose={deleteOnClose}
        title="Delete ServiceNow Configuration"
        body={
          <Alert variant="subtle" status="warning" w="100%">
            <Flex>
              <ErrorSharpIcon color="yellow.100" mr={2} />
              <Box>
                <Text textStyle="body-md-bold">{warning}</Text>
                <Text>{deleteWarning}</Text>
                <Text>{deleteResult}</Text>
              </Box>
            </Flex>
          </Alert>
        }
        footer={
          <ButtonGroup isDisabled={deleteEscalationConfigurationsLoading}>
            <Button variant="solid" colorScheme="gray" onClick={deleteOnClose}>
              {noGoBack}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={async () => {
                await handleDeleteEscalationConfigurations();
                deleteOnClose();
              }}
              isLoading={deleteEscalationConfigurationsLoading}
              isDisabled={deleteEscalationConfigurationsLoading}
            >
              {yesDelete}
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
};
